import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, from, switchMap, throwError } from 'rxjs';

import { AuthenticationService } from './authentication.service';

/**
 * Adds the access token to requests going to our own services -- and only to those.
 *
 * This replaces the library's `resourceServer` option, which could attach a token but never renew
 * one. Under the implicit flow that was enough, because a timer kept the token fresh through a
 * hidden iframe. Without the iframe there is no timer, and renewal has to happen where the need
 * actually arises: at the request.
 *
 * Two guards, and they answer different halves:
 *
 *   * **Before sending**, a session close to expiry is renewed. This is the case a timer would miss
 *     anyway: an access token lives two hours, and someone filling in a long form navigates once at
 *     the beginning and then not again -- so the form outlives the token, and the save would go out
 *     with a header the services refuse.
 *   * **After a 401**, the request is renewed and sent once more. That covers what the first cannot
 *     know about: a token withdrawn at Cardinal, a clock apart from Cardinal's, a renewal that
 *     failed while the tab was in the background. **Once**, never in a loop -- a second 401 is an
 *     answer, not a reason to try again, and it ends at the sign-in.
 *
 * The host check is not a formality. The translations come from this application's own origin and
 * need no token; sending a bearer token to any other host would leak it. So this is a whitelist
 * rather than "everything except".
 */
@Injectable({
  providedIn: 'root'
})
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    private _authenticationService: AuthenticationService,
    private _serviceOrigins: ServiceOrigins
  ) { }

  public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isOwnService(request.url)) {
      return next.handle(request);
    }

    if (this._authenticationService.needsRenewal()) {
      return from(this._authenticationService.renew())
        .pipe(switchMap(() => this.sendAuthorised(request, next)));
    }

    return this.sendAuthorised(request, next);
  }

  private sendAuthorised(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(this.authorised(request))
      .pipe(
        catchError((failure: unknown) => {
          if (!(failure instanceof HttpErrorResponse) || failure.status !== 401) {
            return throwError(() => failure);
          }

          return from(this._authenticationService.renew())
            .pipe(
              switchMap(renewed => {
                if (renewed) {
                  return next.handle(this.authorised(request));
                }

                // The session is gone rather than stale. Cardinal issues reference tokens, so the
                // 401 comes back with an empty body -- there is nothing to show the user, and the
                // honest answer is to sign in again.
                this._authenticationService.redirectToSignIn(window.location.pathname + window.location.search);

                return throwError(() => failure);
              })
            );
        })
      );
  }

  /** A request without a token is sent as it is: an anonymous 401 is more honest than an empty header. */
  private authorised(request: HttpRequest<any>): HttpRequest<any> {
    const token = this._authenticationService.getAccessToken();

    return token
      ? request.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
      : request;
  }

  private isOwnService(url: string): boolean {
    return this._serviceOrigins.origins.some(origin => url.startsWith(origin));
  }
}

/**
 * The hosts the access token may be sent to.
 *
 * A class rather than a plain injection token so the interceptor can take it through the
 * constructor like everything else. The application provides it from its base urls -- deriving it
 * from those rather than from an endpoint list is deliberate: an endpoint added to a new group would
 * otherwise travel without a token, and an unauthenticated request looks like a permission problem
 * rather than a missing header.
 */
export class ServiceOrigins {
  constructor(public readonly origins: readonly string[]) { }
}
